import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import Socket from "socket";
import useSound from "use-sound";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";

import {
  logoVariants,
  logoExitVariants,
  logoEntryVariants,
  fade_bounce_delay_variants
} from "shared/components/animations";
import { getRandomJokes } from "shared/api/jokes";
import { setIsLoading } from "redux/loader/action";
import eyeSmall from "assets/images/vb/vb-eye.png";
import eyeLarge from "assets/images/vb/vb-eye-large.png";
import buzzedIcon from "assets/images/vb/buzzed.png";
import fetchRandomJokes from "shared/api/jokes";
import VBJoining from "./screens/VBJoining";
import VBPreGuess from "./screens/VBPreGuess";
import VBGuessing from "./screens/VBGuessing";
import VBResultTurn from "./screens/VBResultTurn";
import VBOver from "./screens/VBOver";

const VB = () => {
  const dispatch = useDispatch();
  const {
    gameReducer: {
      mobile_app,
      currentUser,
      roomObj: {
        id: roomId,
        users,
        game_state: {
          word,
          teams,
          status,
          current_team,
          banned_words,
          current_round,
          game_parameters: { total_rounds } = {}
        } = {}
      }
    },
    paramsReducer: { gameObj: { app_buzzer_sound } = {} }
  } = useSelector((state) => state);

  const [buzzer_sound] = useSound(app_buzzer_sound);
  const [joke, setJoke] = useState("");
  const [buzzerInfo, setbuzzerInfo] = useState({});

  const currentPlayer = users?.find(
    (user) => user.user_id === currentUser?.userId
  );
  const gusserTeam = teams?.find((item) => item.id === current_team);
  const isQuarterback = gusserTeam?.quarterback === currentPlayer?.player_id;
  const isGuesser = gusserTeam?.team_queue.includes(currentPlayer?.player_id);
  const isDeputy = !isQuarterback && !isGuesser;
  const activeQB = users?.find(
    (item) => item.player_id === gusserTeam?.quarterback
  );
  const team = teams?.find((item) => {
    if (
      item.quarterback === currentPlayer?.player_id ||
      item.team_queue.includes(currentPlayer?.player_id)
    ) {
      return item;
    }
  });

  useEffect(() => {
    getRandomJokes().then((data) => setJoke(data));
  }, []);

  useEffect(() => {
    if (!_.isEmpty(buzzerInfo)) {
      setTimeout(() => {
        setbuzzerInfo({});
      }, 1300);
    }
  }, [buzzerInfo]);

  const handle_buzzer = (res) => {
    if (res.message === "Buzzed") {
      setbuzzerInfo({ name: res.display_name, picture: res.profile_image });
      buzzer_sound();
    }
  };
  const handle_buzzer_error = (err) => {};

  useEffect(() => {
    return Socket.addListener("others", handle_buzzer, handle_buzzer_error);
  }, [buzzer_sound]);

  const handleIsLoading = (value) => {
    dispatch(setIsLoading(value));
  };

  const getRole = () => {
    if (isQuarterback) return "quarterback";
    if (isGuesser) return "guesser";
    if (isDeputy) return "deputy";
  };

  const getTitle = () => {
    if (team?.id === "A") {
      if (getRole() === "deputy") return "Team Alpha: Deputy";
      if (getRole() === "guesser") return "Team Alpha: Guesser";
      if (getRole() === "quarterback") return "Team Alpha: Captain";
    } else {
      if (getRole() === "deputy") return "Team Beta: Deputy";
      if (getRole() === "guesser") return "Team Beta: Guesser";
      if (getRole() === "quarterback") return "Team Beta: Captain";
    }
  };

  const buttonPressAction = (type) => {
    let data = {};
    switch (type) {
      case "update":
        data = {
          roomId: roomId,
          action: "update-status"
        };
        break;
      case "score":
        data = {
          roomId: roomId,
          action: "qb-score-vb",
          word: word
        };
        break;
      case "skip":
        data = {
          roomId: roomId,
          action: "qb-skip-vb",
          word: word
        };
        break;
      case "buzzer":
        data = {
          roomId: roomId,
          action: "buzz-vb",
          word: word
        };
        break;
      default:
        break;
    }
    Socket.sendMessage(data);
  };

  const handleButtonPress = useCallback(
    _.debounce((type) => buttonPressAction(type), 1000, {
      leading: true,
      trailing: false
    }),
    [word, roomId]
  );

  const getEyeVariants = () => {
    if (status === "Joining") {
      return logoEntryVariants;
    }
    if (current_round === total_rounds && status === "ResultRound") {
      return logoExitVariants;
    }
    if (status === "Over") {
      return logoVariants;
    }
  };
  const getExitValue = () => {
    if (current_round === total_rounds && status === "ResultRound") {
      return "exit";
    } else {
      return "";
    }
  };

  return (
    <div className="vb-wrapper">
      <div
        className={`vb-main-container ${
          status === "Joining" ? "no-overflow" : ""
        }`}
      >
        <AnimatePresence exitBeforeEnter>
          <motion.div
            variants={getEyeVariants()}
            initial="hidden"
            animate="visible"
            exit="exit"
            key="vb_top_1"
            className="eye-icon"
          >
            {status === "Over" ? (
              <img alt="" src={eyeLarge} className="" />
            ) : (
              <img alt="" src={eyeSmall} className="" />
            )}
          </motion.div>

          {status === "Joining" ||
          status === "Over" ||
          !_.isEmpty(buzzerInfo) ? (
            <></>
          ) : (
            <motion.h2
              variants={fade_bounce_delay_variants}
              initial="hidden"
              animate="visible"
              exit={getExitValue()}
              key="vb_top_2"
              className={`vb-primary-text ${team?.id === "B" ? "deputy" : ""}`}
            >
              {getTitle()}
            </motion.h2>
          )}
        </AnimatePresence>

        <div
          className={`vb-content ${status === "Over" ? "over" : ""} ${
            !_.isEmpty(buzzerInfo) ? "buzzer" : ""
          }`}
        >
          <AnimatePresence exitBeforeEnter>
            {status === "Joining" && <VBJoining key="vb_1" users={users} />}
            {status === "PreGuess" && (
              <VBPreGuess
                key="vb_2"
                joke={joke}
                role={getRole()}
                activeQB={activeQB}
                onUpdateStatus={handleButtonPress}
              />
            )}
            {status === "Guessing" && (
              <VBGuessing
                key="vb_3"
                word={word}
                joke={joke}
                role={getRole()}
                activeQB={activeQB}
                banned_words={banned_words}
                onButtonPress={handleButtonPress}
              />
            )}
            {status === "ResultTurn" && (
              <VBResultTurn
                key="vb_4"
                joke={joke}
                role={getRole()}
                onUpdateStatus={handleButtonPress}
              />
            )}
            {status === "ResultRound" && (
              <VBResultTurn
                key="vb_5"
                joke={joke}
                role={getRole()}
                isResultRound={true}
                onUpdateStatus={handleButtonPress}
              />
            )}
            {status === "Over" && (
              <VBOver key="vb_6" teams={teams} users={users} />
            )}
            {!_.isEmpty(buzzerInfo) && (
              <div key="vb_7" className="vb-buzzer-container">
                <div className="vb-card-div buzzer">
                  <h2 className="top-text">BUZZED!</h2>
                  <img alt="" src={buzzedIcon} className="buzzer-image" />
                  <span className="by-text">by</span>
                  <img
                    alt=""
                    src={buzzerInfo.picture}
                    className="avatar buzzer"
                  />
                  <h2 className="name buzzer">{buzzerInfo.name}</h2>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default VB;
